import { useCallback, useEffect, useRef } from 'react';

const DEFAULT_THRESHOLD = 0.5;
const DEFAULT_BATCH_DELAY = 100;

export type TrackedElement<T> = {
  ref: Element;
  payload: T;
};

type SingleElementCallbackProps<T> = {
  onVisible: (element: TrackedElement<T>) => void;
  onBatchVisible?: never;
  batchDelay?: never;
};

type MultipleElementsCallbackProps<T> = {
  onBatchVisible: (elements: TrackedElement<T>[]) => void;
  onVisible?: never;
  batchDelay?: number;
};

export type UseIntersectionObserverProps<T> = { threshold?: number } & (
  | SingleElementCallbackProps<T>
  | MultipleElementsCallbackProps<T>
);

export type SetElementProps<T> = {
  ref: Element | unknown;
  payload: T;
};

type UseIntersectionObserverReturn<T> = {
  setElementRef: (props: SetElementProps<T>) => void;
};

/**
 * `useIntersectionObserver` is a React hook that uses the IntersectionObserver API to track
 * the visibility of elements within the viewport. It triggers a callback function when an
 * element becomes visible based on a specified visibility threshold. The callbacks are either triggered in batches
 * or individually.
 *
 * @template T - The type of element being tracked.
 *
 * @param {(element: TrackedElement<T>) => void} [onVisible] - A callback function that is
 *   executed when an element becomes visible. If `onBatchVisible` is not provided, this function receives
 *   a single `TrackedElement` object as a parameter.
 * @param {(elements: TrackedElement<T>[]) => void} [onBatchVisible] - A callback function that is
 *   executed when elements become visible in batch. If provided, the hook triggers this callback instead of `onVisible`.
 * @param {number} [threshold=0.5] - An optional visibility threshold. The callback
 *   is triggered when the visibility of an element passes this threshold (default is 0.5, i.e., 50% visibility).
 * @param {number} [batchDelay=100] - Sets the delay in ms before calling the callback function. Default is 100 ms.
 *
 * @returns {Object} An object containing the `setElementRef` function, which should be used to register
 *   elements to be tracked for visibility.
 *
 * @property {(props: SetElementProps<T>) => void} setElementRef - A function used to register elements
 *   to be tracked for visibility. It takes an object with `node` (the element reference) and `element` (the element data)
 *   as parameters.
 *
 * @example
 * const { setElementRef } = useIntersectionObserver<string>({
 *   onVisible: (element) => {
 *     console.log('Element is visible:', element.element);
 *   },
 * });
 *
 * // In your component's render method:
 * <div ref={(node) => setElementRef({ node, element: 'unique-id-1' })}></div>
 * <div ref={(node) => setElementRef({ node, element: 'unique-id-2' })}></div>
 */
export function useIntersectionObserver<T>({
  onVisible,
  onBatchVisible,
  threshold = DEFAULT_THRESHOLD,
  batchDelay = DEFAULT_BATCH_DELAY
}: UseIntersectionObserverProps<T>): UseIntersectionObserverReturn<T> {
  const elementsToTrackRef = useRef<TrackedElement<T>[]>([]);
  const observedElementsRef = useRef(new Map<Element, boolean>());
  const eventBufferRef = useRef<TrackedElement<T>[]>([]);
  const batchTimerRef = useRef<number | null>(null);

  const setElementRef = ({ ref, payload }: SetElementProps<T>) => {
    if (ref instanceof Element) {
      elementsToTrackRef.current.push({ ref, payload });
    }
  };

  const dispatchBatchedEvents = useCallback(() => {
    if (eventBufferRef.current.length <= 0 || !onBatchVisible) {
      return;
    }

    onBatchVisible(eventBufferRef.current);
    eventBufferRef.current = [];

    if (batchTimerRef.current) {
      clearTimeout(batchTimerRef.current);
      batchTimerRef.current = null;
    }
  }, [onBatchVisible]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(entry => observerCallback(entry)),
      { threshold }
    );

    function observerCallback(entry: IntersectionObserverEntry) {
      if (!entry.isIntersecting || entry.intersectionRatio < threshold) {
        return;
      }

      const target = entry.target;
      const element = elementsToTrackRef.current.find(el => el.ref === target);

      // if no element, or the element has already been observed return
      if (!element || observedElementsRef.current.get(target)) {
        return;
      }

      observedElementsRef.current.set(target, true);
      observer.unobserve(target);

      if (onVisible) {
        onVisible(element);
        return;
      }

      eventBufferRef.current.push(element);

      if (!batchTimerRef.current) {
        batchTimerRef.current = window.setTimeout(dispatchBatchedEvents, batchDelay);
      }
    }

    elementsToTrackRef.current.forEach(element => {
      if (!observedElementsRef.current.has(element.ref)) {
        observer.observe(element.ref);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [batchDelay, dispatchBatchedEvents, elementsToTrackRef, onBatchVisible, onVisible, threshold]);

  return {
    setElementRef
  };
}
